import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';

const classes = {
  IS_ACTIVE: 'is-active'
};

const attributes = {
  SCROLL_LOCK: 'scroll-lock'
};

/**
 * @typedef {Object} Refs
 * @property {HTMLButtonElement} menuTrigger
 * @property {HTMLElement} menuContent
 * @property {HTMLElement} [overlay]
 *
 * @extends {Component<Refs>}
 */

class SidebarMenu extends Component {
  requiredRefs = ['menuTrigger', 'menuContent'];

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('keydown', this.handleKeydown);
    super.disconnectedCallback();
  }

  toggleMenu() {
    this.classList.contains(classes.IS_ACTIVE) ? this.closeMenu() : this.openMenu();
  }

  openMenu() {
    this.#setMenuOpen(true);
  }

  closeMenu() {
    this.#setMenuOpen(false);
  }

  /** @param {boolean} isOpen */
  #setMenuOpen(isOpen) {
    const { menuContent, menuTrigger } = this.refs;

    if (menuContent) {
      menuContent.style.maxHeight = isOpen ? `${menuContent.scrollHeight}px` : '0';
    }

    menuTrigger?.setAttribute('aria-expanded', String(isOpen));
    this.classList.toggle(classes.IS_ACTIVE, isOpen);

    const root = document.documentElement;
    isOpen ? root.setAttribute(attributes.SCROLL_LOCK, '') : root.removeAttribute(attributes.SCROLL_LOCK);

    const docListen = isOpen ? 'addEventListener' : 'removeEventListener';
    document[docListen]('click', this.handleDocumentClick);
    document[docListen]('keydown', this.handleKeydown);

    isOpen ? trapFocus(this, menuTrigger ?? undefined) : removeTrapFocus(menuTrigger ?? undefined);
  }

  /** @param {MouseEvent} e */
  handleDocumentClick = (e) => {
    const target = e.target;

    if (!(target instanceof Node) || !this.contains(target)) {
      this.closeMenu();
    }
  };

  /** @param {KeyboardEvent} e */
  handleKeydown = (e) => {
    if (e.key === 'Escape' && this.classList.contains(classes.IS_ACTIVE)) {
      e.preventDefault();
      this.closeMenu();
    }
  };
}

if (!customElements.get('sidebar-menu')) {
  customElements.define('sidebar-menu', SidebarMenu);
}
